import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import {
  RegistrationsComponent,
  EditableComponent,
} from '@dpr-details/models/component';
import {
  DprDetailsGridBaseComponent,
  DprProgressGridData,
} from '@dpr-details/models/grid';
import { TextDisplayComponent, NumericEditComponent } from '@grid/components';
import {
  GridAlignment,
  GridSortMode,
  GridSortDirection,
  GridCellContentAlignment,
} from '@grid/enums';
import { GridOptions, GridColumn } from '@grid/models';
import { DecimalDisplayComponent } from '@shared/components';
import { ProgressTerminology } from '@shared/models/project-setup';
import { Constants } from '@shared/utils';
import { GridDefaultLabels, GridColumns } from '@shared/utils/grid';

@Component({
  selector: 'app-dpr-progress-grid',
  templateUrl: './dpr-progress-grid.component.html',
  styleUrls: ['./dpr-progress-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprProgressGridComponent
  extends DprDetailsGridBaseComponent<DprProgressGridData>
  implements
    RegistrationsComponent<DprProgressGridData>,
    EditableComponent,
    OnChanges
{
  readonlyProgressGridColumn = {
    header: {
      alignment: GridAlignment.Right,
    },
    columnAlignment: GridAlignment.Right,
    minWidth: 200,
    display: {
      type: DecimalDisplayComponent,
    },
    allowSorting: false,
    allowEditing: false,
    tabIndex: -1,
    disableFocusedBackground: true,
  };

  constructor() {
    super();
  }

  private get progressTerminology(): ProgressTerminology {
    return this.selectedSubproject.data.progress.progressTerminology;
  }

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.predefinedChanges?.currentValue) {
      this.gridComponent?.api?.setPredefinedChanges?.(this.predefinedChanges);
    }

    if (changes.selectedSubproject?.currentValue && this.labeling) {
      this.gridColumns$.next(this.getGridColumns());
    }

    if (changes.canEdit) {
      this.gridOptions$.next(this.getGridOptions());
      this.gridColumns$.next(this.getGridColumns());
    }
  }

  //#endregion

  //#region Private methods

  protected getGridOptions(): GridOptions<DprProgressGridData> {
    return {
      sequenceConfig: {
        disabled: true,
      },
      labeling: GridDefaultLabels,
      addConfig: {
        allowAdding: false,
        keepAutogeneratedIds: true,
      },
      editConfig: {
        allowEditing: this.canEdit,
      },
      sortConfig: {
        mode: GridSortMode.Fixed,
        definition: [
          {
            columnDef: 'priority',
            direction: GridSortDirection.Ascending,
          },
        ],
      },
      saveConfig: {
        isExternalSave: true,
      },
      events: {
        onGridRegistered: this.onGridRegistered.bind(this),
      },
    };
  }

  protected getGridColumns(): GridColumn<DprProgressGridData>[] {
    return [
      {
        columnDef: 'name',
        header: {
          name: this.progressTerminology.block,
          alignment: GridAlignment.Left,
        },
        ...this.readonlyProgressGridColumn,
        display: {
          type: TextDisplayComponent,
        },
        columnAlignment: GridAlignment.Left,
      },
      {
        columnDef: 'clearedToday',
        header: {
          name: this.progressTerminology.clearedToday,
          alignment: GridAlignment.Right,
        },
        minWidth: 200,
        columnAlignment: GridAlignment.Right,
        display: {
          type: DecimalDisplayComponent,
        },
        edit: {
          type: NumericEditComponent,
          config: {
            alignment: GridCellContentAlignment.Right,
            options: {
              allowDecimals: true,
              decimalPrecision: 2,
              min: 0,
              max: Constants.NumericOptions.intMax,
              step: 0.01,
            },
          },
        },
        onCellEdited: (data: DprProgressGridData) => {
          data.clearedTotal =
            (data.clearedTotalInitialValue || 0) + data.clearedToday;
          data.percentComplete = +(
            (data.clearedTotal / data.targetTotal) *
            100
          ).toFixed(2);
          data.targetLeft = data.targetTotal - data.clearedTotal;
        },
      },
      {
        columnDef: 'clearedTotal',
        ...this.readonlyProgressGridColumn,
        header: {
          ...this.readonlyProgressGridColumn.header,
          name: this.progressTerminology.clearedTotal,
        },
        recomputeRowOnChange: true,
      },
      {
        columnDef: 'percentComplete',
        ...this.readonlyProgressGridColumn,
        header: {
          ...this.readonlyProgressGridColumn.header,
          name: this.progressTerminology.percentComplete,
        },
        recomputeRowOnChange: true,
      },
      {
        columnDef: 'targetTotal',
        ...this.readonlyProgressGridColumn,
        header: {
          ...this.readonlyProgressGridColumn.header,
          name: this.progressTerminology.targetTotal,
        },
      },
      {
        columnDef: 'targetLeft',
        ...this.readonlyProgressGridColumn,
        header: {
          ...this.readonlyProgressGridColumn.header,
          name: this.progressTerminology.totalLeft,
        },
        recomputeRowOnChange: true,
      },
      GridColumns.comment,
    ];
  }
}
